import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Trash2 } from 'lucide-react';
import { Button, Modal } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';

import ActionsIcon from 'pages/contactsExtended/bulkActions/actionsIcon';
import { useBulkDelete } from '../hooks/useBulkDelete';

export const ActionDelete = ({ selectedRows, networkId, onDeleted }) => {
  const { showConfirm, isLoading, count, label, title, confirmCta, open, close, confirm } = useBulkDelete({
    selectedRows,
    networkId,
    onDeleted,
  });

  // BulkActionsBar uses `transform`, which creates a containing block that
  // traps `position: fixed` descendants. Portal the Modal to document.body so
  // it overlays the page instead of being nested inside the bar.
  const modal = showConfirm && (
    <Modal
      title={title}
      isOpen
      onCancel={close}
      actions={
        <Flex sx={{ gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button data-testid="bulk-delete-confirm" variant="danger" onClick={confirm} loading={isLoading}>
            {confirmCta}
          </Button>
        </Flex>
      }
    >
      <Text>Deleting {count > 1 ? 'these lists' : 'this list'} will:</Text>
      <Box as="ul" px="24px" pt="8px">
        <Box as="li">Permanently delete the {label} themselves</Box>
        <Box as="li">Turn off shared access on any that are currently active</Box>
        <Box as="li">Not remove any contacts from your network</Box>
      </Box>
      <Text mt="3" fontWeight={500}>
        <Text as="span" mr="2">
          ⚠️
        </Text>
        This action cannot be undone.
      </Text>
    </Modal>
  );

  return (
    <>
      <ActionsIcon icon={Trash2} text="Delete" onClick={open} />
      {modal && createPortal(modal, document.body)}
    </>
  );
};

ActionDelete.propTypes = {
  selectedRows: PropTypes.arrayOf(PropTypes.number).isRequired,
  networkId: PropTypes.number.isRequired,
  onDeleted: PropTypes.func,
};

ActionDelete.defaultProps = {
  onDeleted: undefined,
};

export default ActionDelete;
